import { COMPETITOR_POS } from '../../../../shared/enums';
import { GOOGLE_PHONE_SOURCES } from '../../../../shared/fields';
import type { IsoDate, Signal } from '../../../../shared/types';
import { copyText } from '../../data/drafts';
import { formatDate, todayLocal } from '../../logic/dates';
import type { LatLng } from '../../logic/route';
import { distanceText, eventText, filedAgo, hiddenText, whyText } from '../../logic/signalRank';
import { signalEventLabel, signalSource } from '../../logic/signals';
import { btn } from '../../ui/components';
import { h, span } from '../../ui/dom';
import { POS_LABELS, SIGNAL_SOURCE_LABELS } from '../../ui/labels';

/** "From ABC liquor license #683462", linked to the official record so the rep can check it. An HdL
 *  city has no page per license: the link opens its search and copies the account # to paste there. */
const sourceLine = (s: Signal): HTMLElement => {
  const { text, url, copy } = signalSource(s);
  const copied = copy ? h('div', { class: 'sig__copied', hidden: true }) : null;
  const onClick = copy
    ? () =>
        void copyText(copy).then((ok) => {
          if (!ok || !copied) return;
          copied.textContent = `Account # ${copy} copied. The city site has no page per license: search by Account # and paste it.`;
          copied.hidden = false;
        })
    : undefined;
  return h(
    'div',
    { class: 'sig__source' },
    'From ',
    h('a', { href: url, target: '_blank', rel: 'noopener', onClick }, text),
    copied,
  );
};

/** A source whose records carry no phone: what the card shows came from Google (D59–D61). */
const fromGoogle = (s: Signal): boolean => (GOOGLE_PHONE_SOURCES as readonly string[]).includes(s.source);

const phoneLink = (phone: string): HTMLElement =>
  h('a', { href: `tel:${phone.replace(/[^\d+]/g, '')}` }, phone);

/** Web addresses only, named by their site: "7-eleven.com". */
const siteLink = (url: string | null): HTMLElement | null => {
  const site =
    url && /^https?:\/\//i.test(url) ? url.replace(/^https?:\/\/(www\.)?/i, '').split(/[/?#]/)[0] : null;
  return url && site ? h('a', { href: url, target: '_blank', rel: 'noopener' }, site) : null;
};

/** The record's contact, tap to call or write: a plan check's contact (D58), or the people on an ABC
 *  license with what Google lists for the business, said as such (D59). Gone after triage. */
const contactLine = (s: Signal): HTMLElement | null => {
  const parts: (string | HTMLElement)[] = [];
  if (s.contact_name) parts.push(s.contact_name);
  const found = [s.contact_phone ? phoneLink(s.contact_phone) : null, siteLink(s.contact_website)].filter(
    (el): el is HTMLElement => el !== null,
  );
  if (fromGoogle(s) && found.length)
    parts.push(
      h(
        'span',
        {},
        'Google: ',
        found.flatMap((el, i) => (i ? [', ', el] : [el])),
      ),
    );
  else parts.push(...found);
  if (s.contact_email) parts.push(h('a', { href: `mailto:${s.contact_email}` }, s.contact_email));
  if (s.contact_address) parts.push(`Mail: ${s.contact_address}`);
  if (parts.length === 0) return null;
  return h(
    'div',
    { class: 'sig__contact' },
    'Contact: ',
    parts.flatMap((p, i) => (i ? [' · ', p] : [p])),
  );
};

export interface SignalCardOpts {
  picked: boolean;
  onPick: (picked: boolean) => void;
  onAdd: (ev: MouseEvent) => void;
  onDismiss: (ev: MouseEvent) => void;
  /** Add + plan and the … sheet (D74); a card without them shows Add and Dismiss alone. */
  onAddPlan?: (ev: MouseEvent) => void;
  onMore?: (ev: MouseEvent) => void;
  /** A hidden card's way back (D72). */
  onShow?: (ev: MouseEvent) => void;
  /** On a card hidden by a learned chain: the chain out of the list, every match back (D72). */
  onNotAChain?: (ev: MouseEvent) => void;
  today?: IsoDate;
  /** Where the rep is (D80): the address line ends with the distance when the card has coordinates. */
  anchor?: LatLng | null;
}

/** Hidden by a name on the chain list, not by a store number: there is a list entry to take back. */
const learnedChain = (s: Signal): boolean => {
  const hide = s.triage?.hide;
  return hide?.reason === 'chain' && !(hide.value ?? '').startsWith('store number');
};

/** What Google says of the business, once asked (D79). */
const googleFact = (s: Signal): string | null => {
  if (s.google_status === 'open') {
    const n = s.google_reviews ?? 0;
    return `Open on Google · ${n} review${n === 1 ? '' : 's'}`;
  }
  if (s.google_status === 'not_found') return 'Not on Google yet';
  if (s.google_status === 'closed_temporarily') return 'Closed for now on Google';
  return null;
};

/** The POS its website runs on (D79): a competitor's to win, Square struck through (a customer). */
const posFact = (s: Signal): HTMLElement | null => {
  const pos = s.detected_pos;
  if (pos === 'square') return span('posbadge posbadge--square posbadge--out', POS_LABELS.square);
  return pos && COMPETITOR_POS.includes(pos) ? span(`posbadge posbadge--${pos}`, POS_LABELS[pos]) : null;
};

/** The facts a rep decides on in three seconds: a phone, a website, several locations, what Google
 *  says, the POS (D74, D79). Absence is the norm, so a missing fact shows nothing. */
const factsRow = (s: Signal): HTMLElement | null => {
  const facts = [
    s.contact_phone ? 'Phone' : null,
    s.contact_website && /^https?:\/\//i.test(s.contact_website) ? 'Website' : null,
    (s.group_count ?? 1) > 1 ? `${s.group_count} locations` : null,
    googleFact(s),
  ]
    .filter((f): f is string => f !== null)
    .map((f) => span('posbadge', f));
  const pos = posFact(s);
  if (pos) facts.push(pos);
  return facts.length ? h('div', { class: 'sig__facts' }, facts) : null;
};

/** "Also: Irvine business license #260003397 (filed Aug 21)": the rows merged into this card (D72). */
const alsoLine = (s: Signal, today: IsoDate): HTMLElement | null =>
  s.siblings?.length
    ? h(
        'div',
        { class: 'sig__also' },
        'Also: ',
        s.siblings
          .map((x) => `${SIGNAL_SOURCE_LABELS[x.source]} #${x.ref} (filed ${formatDate(x.filed_on, today)})`)
          .join(' · '),
      )
    : null;

const actions = (s: Signal, opts: SignalCardOpts): HTMLElement =>
  h(
    'div',
    { class: 'sig__actions' },
    s.bucket === 'hidden'
      ? [
          opts.onShow ? btn('Show', { variant: 'primary', size: 'sm', onClick: opts.onShow }) : null,
          opts.onNotAChain && learnedChain(s)
            ? btn('Not a chain', { variant: 'ghost', size: 'sm', onClick: opts.onNotAChain })
            : null,
          btn('Dismiss', { variant: 'ghost', size: 'sm', onClick: opts.onDismiss }),
        ]
      : [
          opts.onAddPlan
            ? btn('Add + plan', { variant: 'primary', size: 'sm', onClick: opts.onAddPlan })
            : null,
          btn('Add', { variant: opts.onAddPlan ? 'ghost' : 'primary', size: 'sm', onClick: opts.onAdd }),
          btn('Dismiss', { variant: 'ghost', size: 'sm', onClick: opts.onDismiss }),
          opts.onMore
            ? btn('…', { variant: 'ghost', size: 'sm', ariaLabel: 'More', onClick: opts.onMore })
            : null,
        ],
  );

/** One new business, as the sort read it (D74): what it is, where, how long ago, the facts, the why —
 *  or why it is hidden — the record's contact and source, and what to do about it. */
export const signalCard = (s: Signal, opts: SignalCardOpts): HTMLElement => {
  const today = opts.today ?? todayLocal();
  const filed = filedAgo(s.filed_on, today);
  const hidden = s.bucket === 'hidden';
  const why = hidden ? hiddenText(s) : whyText(s);
  const where = [s.address, s.city].filter(Boolean).join(', ') || 'No street address';
  const distance = distanceText(s, opts.anchor ?? null);
  return h(
    'article',
    { class: hidden ? 'sig sig--hidden' : 'sig', 'data-id': s.id },
    h(
      'label',
      { class: 'sig__pick' },
      h('input', {
        type: 'checkbox',
        checked: opts.picked,
        'aria-label': `Pick ${s.business_name} for a route`,
        onChange: (ev: Event) => opts.onPick((ev.currentTarget as HTMLInputElement).checked),
      }),
    ),
    h(
      'div',
      { class: 'sig__body' },
      h(
        'div',
        { class: 'sig__head' },
        span('sig__name', s.business_name),
        span(`sig__event sig__event--${s.event}`, signalEventLabel(s)),
      ),
      h('div', { class: 'sig__kind' }, eventText(s)),
      h('div', { class: 'sig__addr' }, distance ? `${where} · ${distance}` : where),
      h('div', { class: filed.old ? 'sig__filed sig__filed--old' : 'sig__filed' }, filed.text),
      factsRow(s),
      s.non_merchant ? h('div', { class: 'sig__why' }, `Probably not a merchant${s.non_merchant_types?.length ? ` · ${s.non_merchant_types.join(', ')}` : ''}`) : null,
      why ? h('div', { class: hidden ? 'sig__hidden' : 'sig__why' }, why) : null,
      contactLine(s),
      sourceLine(s),
      alsoLine(s, today),
      actions(s, opts),
    ),
  );
};
